import { crudOperations } from './api';

// Helper function to format address from structured fields
const formatAddress = (customer) => {
  const parts = [];
  
  if (customer.address_line1) parts.push(customer.address_line1);
  if (customer.address_line2) parts.push(customer.address_line2);
  if (customer.address_city) parts.push(customer.address_city);
  if (customer.address_county) parts.push(customer.address_county);
  if (customer.address_postcode) parts.push(customer.address_postcode);
  
  // Only include country if it's not UK (default)
  if (customer.address_country && customer.address_country !== 'United Kingdom') {
    parts.push(customer.address_country);
  }
  
  return parts.join(', ');
};

// Helper function to check if customer has address
const hasAddress = (customer) => {
  return !!(customer.address_line1 || customer.address_line2 || customer.address_city || 
           customer.address_county || customer.address_postcode);
};

class CustomerService {
  // Get customers for a franchisee
  async getCustomers(franchiseeId, { page = 1, limit = 200, search, source, branch_id, sortBy = 'created_at', sortOrder = 'DESC' } = {}) {
    try {
      const params = { page, limit, sortBy, sortOrder };
      if (search) params.search = search;
      if (source) params.source = source;
      if (branch_id) params.branch_id = branch_id;

      const response = await crudOperations.get(`/customers/${franchiseeId}`, params);
      return response.data;
    } catch (error) {
      console.error('Get customers error:', error);
      throw error;
    }
  }

  // Get customer count for a franchisee
  async getCustomerCount(franchiseeId) {
    try {
      const response = await crudOperations.get(`/customers/${franchiseeId}/count`);
      return response.data.total;
    } catch (error) {
      console.error('Get customer count error:', error);
      throw error;
    }
  }

  // Get specific customer
  async getCustomer(franchiseeId, customerId) {
    try {
      const response = await crudOperations.get(`/customers/${franchiseeId}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Get customer error:', error);
      throw error;
    }
  }

  // Get customer by QuickBooks customer ID
  async getCustomerByQbId(franchiseeId, qbCustomerId) {
    try {
      const response = await crudOperations.get(`/customers/${franchiseeId}/by-qb-id/${qbCustomerId}`);
      return response.data;
    } catch (error) {
      console.error('Get customer by QB ID error:', error);
      throw error;
    }
  }

  // Create new customer
  async createCustomer(franchiseeId, customerData) {
    try {
      const response = await crudOperations.post(`/customers/${franchiseeId}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Create customer error:', error);
      throw error;
    }
  }

  // Update customer
  async updateCustomer(franchiseeId, customerId, customerData) {
    try {
      const response = await crudOperations.put(`/customers/${franchiseeId}/${customerId}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Update customer error:', error);
      throw error;
    }
  }

  // Delete customer
  async deleteCustomer(franchiseeId, customerId) {
    try {
      const response = await crudOperations.delete(`/customers/${franchiseeId}/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Delete customer error:', error);
      throw error;
    }
  }

  // Delete all customers for a franchisee (admin only, for testing)
  async deleteAllCustomers(franchiseeId) {
    try {
      const response = await crudOperations.delete(`/customers/${franchiseeId}/all`);
      return response.data;
    } catch (error) {
      console.error('Delete all customers error:', error);
      throw error;
    }
  }

  // Search customers (with pagination for infinite scroll)
  async searchCustomers(franchiseeId, query, limit = 20, page = 1) {
    try {
      const response = await crudOperations.get(`/customers/${franchiseeId}/search`, {
        q: query,
        limit,
        page
      });
      return response.data;
    } catch (error) {
      console.error('Search customers error:', error);
      throw error;
    }
  }

  // Validate customer data
  validateCustomerData(customerData, isUpdate = false) {
    const errors = [];

    // Name validation
    if (!isUpdate || customerData.name !== undefined) {
      if (!customerData.name || customerData.name.trim().length < 2) {
        errors.push('Customer name must be at least 2 characters long');
      }
      if (customerData.name && customerData.name.length > 255) {
        errors.push('Customer name must not exceed 255 characters');
      }
    }

    // Email validation (optional)
    if (customerData.email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email)) {
        errors.push('Please provide a valid email address');
      }
    }

    // Phone validation (optional)
    if (customerData.phone) {
      if (!/^[\+]?[1-9][\d]{0,15}$/.test(customerData.phone)) {
        errors.push('Please provide a valid phone number');
      }
    }

    // Address validation (optional)
    if (customerData.address && customerData.address.length > 1000) {
      errors.push('Address must not exceed 1000 characters');
    }

    // Company name validation (optional)
    if (customerData.company_name && customerData.company_name.length > 255) {
      errors.push('Company name must not exceed 255 characters');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Format customer data for display
  formatCustomerForDisplay(customer) {
    return {
      ...customer,
      displayName: customer.name || 'Unknown Customer',
      sourceDisplay: this.formatSource(customer.source),
      createdAtDisplay: customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'Unknown',
      qbCreatedAtDisplay: customer.qb_created_date ? new Date(customer.qb_created_date).toLocaleDateString() : null,
      hasEmail: !!customer.email,
      hasPhone: !!customer.phone,
      hasAddress: hasAddress(customer),
      isQuickBooksCustomer: customer.source === 'quickbooks'
    };
  }

  // Format source for display
  formatSource(source) {
    const sourceMap = {
      manual: 'Manual Entry',
      quickbooks: 'QuickBooks Import'
    };
    return sourceMap[source] || source;
  }

  // Get customer initials for avatar
  getCustomerInitials(customer) {
    if (!customer || !customer.name) return '?';
    
    const names = customer.name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }

  // Sort customers by various criteria
  sortCustomers(customers, sortBy = 'name', sortOrder = 'asc') {
    return [...customers].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle dates
      if (sortBy.includes('_date') || sortBy.includes('_at')) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'desc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'desc' ? -1 : 1;

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    });
  }

  // Filter customers by search term
  filterCustomers(customers, searchTerm) {
    if (!searchTerm) return customers;

    const search = searchTerm.toLowerCase();
    return customers.filter(customer =>
      customer.name?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search) ||
      customer.phone?.includes(search) ||
      formatAddress(customer).toLowerCase().includes(search) ||
      customer.company_name?.toLowerCase().includes(search)
    );
  }

  // Get customer statistics
  getCustomerStats(customers) {
    const stats = {
      total: customers.length,
      withEmail: 0,
      withPhone: 0,
      withAddress: 0,
      quickbooksImported: 0,
      manuallyAdded: 0
    };

    customers.forEach(customer => {
      if (customer.email) stats.withEmail++;
      if (customer.phone) stats.withPhone++;
      if (hasAddress(customer)) stats.withAddress++;
      if (customer.source === 'quickbooks') stats.quickbooksImported++;
      if (customer.source === 'manual') stats.manuallyAdded++;
    });

    return stats;
  }

  // Export customers to CSV
  exportToCSV(customers) {
    const headers = [
      'Name',
      'Email', 
      'Phone',
      'Address',
      'Company',
      'Source',
      'Created Date',
      'QB Created Date'
    ];

    const csvData = customers.map(customer => [
      customer.name || '',
      customer.email || '',
      customer.phone || '',
      formatAddress(customer) || '',
      customer.company_name || '',
      customer.source || '',
      customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '',
      customer.qb_created_date ? new Date(customer.qb_created_date).toLocaleDateString() : ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  }

  // Download CSV file
  downloadCSV(customers, filename = 'customers.csv') {
    const csvContent = this.exportToCSV(customers);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Check if customer can be deleted
  canDeleteCustomer(customer, jobs = []) {
    const customerJobs = jobs.filter(job => job.customer_id === customer.id);
    
    if (customerJobs.length > 0) {
      return {
        canDelete: false,
        reason: `Customer has ${customerJobs.length} associated job(s). Delete jobs first.`
      };
    }

    return { canDelete: true };
  }
}

// Create singleton instance
const customerService = new CustomerService();

export default customerService;